import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Img from 'src/components/shared/Img'
import RichText from '../../../RichText'
import PrimaryButton from 'src/components/shared/PrimaryButton'
import ProvenResultList from './ProvenResultList'
import { useLeadId } from '../../../../../common/lead-id'
import useOnViewport from '../../../../../common/hooks/useOnViewport'
import { useAnalytics } from 'src/common/analytics'
import { APPLY_FDR_URL } from '../../../../../common/constants'
import { useRouter } from 'next/router'
import { buildQueryString } from '../../../../../common/utils'
import TrustpilotItem from '../../../TrusPilotItem'
import { ProvenResultsBelowTheFold } from '../../../ProvenResults'
import { TrustPilot } from 'src/components/shared/TrustMarkerWidgets'
import { ScheduleACallCTA } from 'src/components/shared/ScheduleACallCTA'

const HeroSection = (props) => {
  const {
    title,
    description,
    img,
    provenResult,
    btnName,
    btnUrl,
    showTrustpilot,
    showNewTrustpilot,
    provenResultsBelowTheFold,
    phone = '',
    heroContainerClassName = '',
    contentClassName = '',
    provenResultsClassName = '',
    imgClassName = '',
    imgContainerClassName = '',
    imgContainerHeightClassName = 'h-520',
    richTextClassName = '',
    trustPilotInfo = {},
    /** Optional SSR / parent overrides; TrustpilotItem still merges with `useTrustMarkers()`. */
    trustpilotReviews,
    trustpilotScore,
    buttonColor = '',
    phoneButtonColor = '',
    phoneButtonTextColor = '',
    showScheduleACallButton = false,
  } = props
  const [isHeroCardVisible, isCtaButtonUnderViewport] = useOnViewport(
    'heroPrimaryBtnContent'
  )
  const { track } = useAnalytics()

  const handlePreviewClick = () => {
    track(
      {
        event_type: 'track',
        click_id: 'Trustpilot',
        click_type: 'Link Click',
        nav_link_section: 'Hero Card',
        additional_info: {
          section: 'trust-marker-truspilot',
          click_url: trustPilotInfo?.link || '',
        },
      },
      {},
      'button_click'
    )
  }

  const showStickyBtn = useMemo(
    () => !isHeroCardVisible && !isCtaButtonUnderViewport,
    [isHeroCardVisible, isCtaButtonUnderViewport]
  )

  const leadId = useLeadId()
  const { query: baseQuery } = useRouter()

  let query = useMemo(
    () => buildQueryString(baseQuery, leadId),
    [baseQuery, leadId]
  )

  return (
    <>
      <div
        id="hero-card"
        className={`${heroContainerClassName} gap-x-primary relative grid grid-cols-12`}
      >
        <div
          className={`absolute z-50 col-span-full my-auto flex flex-col justify-center justify-self-center rounded-xl bg-white p-5 text-center shadow-gray-400 drop-shadow-xl lg:relative lg:col-span-6 lg:m-0 lg:rounded-none lg:bg-none lg:p-0 lg:text-left lg:drop-shadow-none xl:col-span-5 xl:col-start-2 ${contentClassName}`}
        >
          <h1
            className="text-3.5xl font-bold leading-11 text-black-base lg:whitespace-pre-line lg:text-57 lg:leading-15"
            data-testid="brand-hero-title"
          >
            {title}
          </h1>
          <RichText
            contentData={description}
            className={`whitespace-normal pt-3 pb-7 text-15 text-black-base-light lg:whitespace-pre-line lg:pt-8 lg:pb-10 lg:text-lg ${richTextClassName}`}
            data-testid="brand-hero-description"
          />
          <div
            id="heroPrimaryBtnContent"
            className="flex w-full flex-col items-center self-center text-center lg:flex-row lg:items-start lg:gap-4 lg:self-start "
            data-testid="brand-hero-btn-wrapper"
          >
            <div className="flex gap-4">
              <div className="flex flex-col items-center justify-center">
                <PrimaryButton
                  id="heroPrimaryButton"
                  heroBtn={true}
                  sectionTitle={title}
                  title={btnName}
                  href={`${btnUrl}?${query}`}
                  className="w-[278px] border-2 border-red-850 bg-red-850 px-4.5 py-2.5 text-lg font-bold text-white lg:px-6"
                  data-testid="brand-hero-btn"
                />
                {showScheduleACallButton && (
                  <ScheduleACallCTA
                    nav_link_section="Schedule a call component"
                    page_section="Hero Card"
                    variant="outlined"
                    className="mt-3 block h-13 w-full px-11 text-lg lg:hidden"
                  >
                    Schedule a call
                  </ScheduleACallCTA>
                )}
                {showNewTrustpilot ? (
                  <div
                    className="hidden lg:block"
                    data-testid="brand-hero-trustpilot"
                  >
                    <TrustPilot
                      {...trustPilotInfo}
                      loading="eager"
                      onReviewClick={handlePreviewClick}
                    />
                  </div>
                ) : null}
              </div>
              {showScheduleACallButton && (
                <ScheduleACallCTA
                  nav_link_section="Schedule a call component"
                  page_section="Hero Card"
                  variant="outlined"
                  className="hidden h-13 px-11 text-lg lg:block"
                >
                  Schedule a call
                </ScheduleACallCTA>
              )}
            </div>

            {phone && !showScheduleACallButton ? (
              <PrimaryButton
                id="heroPhoneButton"
                sectionTitle={title}
                className={`max-h-13 mt-3 border-2 ${
                  phoneButtonColor ? phoneButtonColor : 'border-blue-base'
                } px-5 py-2.5 text-lg font-bold ${
                  phoneButtonTextColor ? phoneButtonTextColor : 'text-blue-base'
                } w-[278px] lg:mt-0 lg:w-[230px]`}
                title={`Call ${phone}`}
                href={`tel:${phone}`}
                heroBtn
              />
            ) : null}

            {showNewTrustpilot ? (
              <div className="lg:hidden">
                <TrustPilot
                  {...trustPilotInfo}
                  loading="eager"
                  onReviewClick={handlePreviewClick}
                />
              </div>
            ) : null}
          </div>
          {showTrustpilot ? (
            <TrustpilotItem
              starClassName="min-w-4.5 h-4.5 min-h-4.5 w-4.5 md:h-6 md:w-6 lg:h-7 lg:w-7"
              trustpilotRatingClassName="text-xs md:text-base md:pt-1.5 lg:pt-2.5"
              trustScoreClassName="text-sm lg:text-base"
              trustpilotClassName="text-sm md:text-lg lg:text-23"
              className="mt-4 lg:mt-12 lg:mb-8"
              starsGroupClassName="h-5 min-h-5 w-15 min-w-15 md:h-8 md:w-23 lg:h-6 lg:w-28 mx-1 lg:mx-3"
              starsGroupImgClassName="pt-1.5 md:pt-2 lg:pt-0.5 "
              truspilotReviews={trustpilotReviews}
              truspilotScore={trustpilotScore}
            />
          ) : null}
        </div>
        <div
          className={`relative col-span-full ${imgContainerHeightClassName} overflow-hidden lg:col-span-5 lg:col-start-8 lg:row-span-2 ${imgContainerClassName}`}
        >
          <Img
            src={img.src}
            alt={img.alt}
            imgClassName={imgClassName}
            layout="fill"
            objectFit="cover"
            priority={true}
            styles={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
            }}
            sizes="(max-width: 640px) 100vw, 50vw"
          />
        </div>

        {provenResult?.list ? (
          <div
            className={`col-span-full flex flex-col bg-blue-base text-center text-white lg:col-span-6 lg:bg-white lg:pt-9 lg:text-base xl:col-span-5 xl:col-start-2 ${provenResultsClassName}`}
          >
            <b className="px-4 text-2.5xl lg:hidden">
              Proven results over the years
            </b>
            <ProvenResultList list={provenResult.list} />
          </div>
        ) : null}
      </div>
      {provenResultsBelowTheFold ? (
        <ProvenResultsBelowTheFold data={provenResultsBelowTheFold} />
      ) : null}
    </>
  )
}

HeroSection.defaultProps = {
  btnName: 'Get debt help now',
  btnUrl: APPLY_FDR_URL(),
  provenResultsClassName: 'pt-24',
}

HeroSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.object.isRequired,
  btnName: PropTypes.string,
  btnUrl: PropTypes.string,
  img: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  }).isRequired,
  provenResult: PropTypes.shape({
    list: PropTypes.array.isRequired,
    showInMobile: PropTypes.bool.isRequired,
  }),
  phone: PropTypes.string,
  heroContainerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  imgClassName: PropTypes.string,
  imgContainerClassName: PropTypes.string,
  showTrustpilot: PropTypes.bool,
  trustpilotReviews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trustpilotScore: PropTypes.number,
  provenResultsBelowTheFold: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.string.isRequired,
      tagline: PropTypes.string.isRequired,
      sup: PropTypes.string,
    })
  ),
  provenResultsClassName: PropTypes.string,
}

export default HeroSection
